import {
  CreateHarmReportRequest,
  CreateReportResponse,
  GetUsersResponse,
  HarmReport,
  InterventionDecision,
  InterventionStatus,
  PlayerProfile,
  ReportSignature,
  UpdateHarmReportResponse,
  UserType,
} from '@inplay/models';
import FullscreenModal from '../fullscreenModal/fullscreenModal';
import Input from '../Input/input';
import styles from './harmReportForm.module.css';
import clock from '../../assets/clock.png';
import money from '../../assets/money.png';
import face from '../../assets/face.png';
import dice from '../../assets/dice.png';
import Button from '../button/button';
import { useOrganizationContext } from '../../service/context/organizationContext';
import { combineDateAndTime, getFormattedTimeValues } from '../../utils/helper';
import { ChangeEvent, useEffect, useState } from 'react';
import DatePickerV2 from '../datePickerV2/datePicker';
import { Mode } from '../datePickerV2/datePicker.type';
import TimePickerV3 from '../timePickerV3/timePicker';
import { Trans, useTranslation } from 'react-i18next';
import {
  lengthOfPlayOptions,
  moneyOptions,
  behaviourOptions,
  otherOptions,
} from '../reportDetails/constants';
import { useUsersSummary } from '../../hooks/useUserData';
import { Option } from '../dropDownInput/dropDownInput.type';
import { DateRange } from 'react-day-picker';
import { useUserDetailContext } from '../../service/context/userDetailContext';
import moment from 'moment';
import SignatureModal from '../signatureModal/signatureModal';
import { FormState, Props } from './harmReportForm.types';
import ButtonSpinner from '../../assets/buttonLoading/buttonLoading';
import { toastV2 } from '../toastV2/toastV2';
import { ReportService } from '../../service/reportService';
import { UserService } from '../../service/userService';
import MultiSelectDropdown from '../multiSelectDropdown/multiSelectDropdown';
import RadioGroupV2 from '../radioGroup';
import {
  decimalToOrdinal,
  isAutoGeneratedReport,
  timeStringToMinutes,
} from './harmReportFormHelper';
import dayjs, { Dayjs } from 'dayjs';

const hasSectionMandatoryFields = (formState: FormState) => {
  const hasCommonMandatory =
    formState.harmIdentifiedBy !== '' &&
    formState.harmDate !== undefined &&
    formState.harmTime !== '' &&
    formState.harmInstance.trim() !== '' &&
    formState.description.trim() !== '' &&
    formState.notesObservation.trim() !== '' &&
    (formState.lengthOfPlay.length > 0 ||
      formState.money.length > 0 ||
      formState.behaviour.length > 0 ||
      formState.other.length > 0);

  const hasManagerMandatory =
    formState.userType === UserType.VENUE_STAFF
      ? true
      : formState.interventionDecision !== null;

  return {
    hasCommonMandatory,
    hasManagerMandatory,
  };
};

const isFormFullyValid = (formState: FormState): boolean => {
  const { hasCommonMandatory, hasManagerMandatory } =
    hasSectionMandatoryFields(formState);

  const hasCreatedBySignature = !!formState.createdBySignature;
  const hasReviewedBySignature =
    formState.userType === UserType.VENUE_STAFF
      ? true
      : !!formState.reviewedBySignature;

  return (
    (hasCommonMandatory && hasCreatedBySignature) ||
    hasManagerMandatory ||
    hasReviewedBySignature
  );
};

const isSameDayAsToday = (d: Dayjs) => d.isSame(dayjs(), 'day');

const HarmReportForm = (props: Props) => {
  const { t } = useTranslation();
  const { selectedVenueBranch } = useOrganizationContext();
  const isAutoGenerated: boolean = isAutoGeneratedReport(props.data);
  const [showCompletedByAddSignature, setShowCompletedByAddSignature] =
    useState(false);
  const [showReviewedByAddSignature, setShowReviewedByAddSignature] =
    useState(false);
  const { usersSummary } = useUsersSummary(true);
  const { userDetail } = useUserDetailContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs(new Date()));
  const [time, setTime] = useState<Dayjs | null>(dayjs(new Date()));

  // Derive the report author: prefer verified user over logged-in user
  const reportAuthor: GetUsersResponse | undefined = (() => {
    if (!props.verifiedUser) return userDetail;
    if (props.verifiedUser.userId === userDetail?.id) return userDetail;
    const found = usersSummary?.data?.find(
      (u: GetUsersResponse) => u.id === props.verifiedUser?.userId,
    );
    if (found) return found;
    return {
      id: props.verifiedUser.userId,
      firstName: props.verifiedUser.firstName,
      lastName: props.verifiedUser.lastName,
      type: props.verifiedUser.type,
      email: props.verifiedUser.email,
    } as GetUsersResponse;
  })();

  // Fetch signature for verified user if different from logged-in user
  const [verifiedUserSignature, setVerifiedUserSignature] =
    useState<string>('');
  useEffect(() => {
    if (
      props.verifiedUser &&
      props.verifiedUser.userId !== userDetail?.id &&
      !reportAuthor?.signature
    ) {
      new UserService()
        .getUserSignature(props.verifiedUser.userId)
        .then((sig) => setVerifiedUserSignature(sig));
    }
  }, [props.verifiedUser, userDetail?.id]);

  // Merge fetched signature into reportAuthor
  const reportAuthorWithSignature: GetUsersResponse | undefined = reportAuthor
    ? {
        ...reportAuthor,
        signature: reportAuthor.signature || verifiedUserSignature,
      }
    : undefined;

  useEffect(() => {
    if (reportAuthor) {
      setFormState((prev: FormState) => {
        return {
          ...prev,
          harmIdentifiedBy: reportAuthor.id ?? '',
        };
      });
    }
  }, [reportAuthor]);

  const formattedTimeValues = getFormattedTimeValues(
    {
      uninterruptedTime: props.data
        ? props.data.playerDetails.gambling.uninterruptedTime
        : Number(props.player?.currentUninterruptedTime),
      totalTimeInGameArea: props.data
        ? props.data.playerDetails.gambling.totalTimeInGameArea
        : Number(props.player?.minutesInGamingRoom),
      totalTimeOutsideGameArea: props.data
        ? props.data.playerDetails.gambling.totalTimeOutsideGameArea
        : Number(props.player?.minutesOutsideGamingRoom),
    },
    t,
  );

  const initialFormState: FormState = props.data
    ? {
        userType: reportAuthor?.type,
        venueName: selectedVenueBranch?.name ?? '',
        harmIdentifiedBy: reportAuthor?.id ?? '',
        harmDate: props.data.identifiedAt,
        harmTime: dayjs(props.data.identifiedAt).format('HH:mm'),
        harmInstance: props.data.harmInstanceCountForPlayer,
        nickname: props.data.playerDetails.name,
        description: props.data.playerDetails.description,
        currentUninterruptedTime: formattedTimeValues.currentUninterruptedTime,
        gamblingAtmUse:
          props.data.playerDetails.gambling.gamblingATMUse.toString(),
        currentConsecutiveSweeps: props.consecutiveSweeps ?? '0',
        totalInterventionsOffered:
          props.data.playerDetails.gambling.interventionsOffered,
        totalInterventionsRequested:
          props.data.playerDetails.gambling.interventionsRequested,
        totalTimeInGambling: formattedTimeValues.totalTimeInGambling,
        totalTimeOutside: formattedTimeValues.totalTimeOutside,
        lengthOfPlay: props.data.signsOfGamblingHarm?.lengthOfPlay,
        money: props.data.signsOfGamblingHarm?.money,
        behaviour: props.data.signsOfGamblingHarm?.behaviour,
        other: props.data.signsOfGamblingHarm?.other,
        notesObservation: props.data.createdBy?.notes ?? '',
        createdBySignature: props.data.createdBy ?? null,
        interventionDecision:
          props.data.intervention.required !== undefined
            ? props.data.intervention.required
              ? 'yes'
              : 'no'
            : '',
        comments: props.data.reviewedBy?.notes ?? '',
        reviewedBySignature: props.data.reviewedBy ?? null,
      }
    : {
        // Player info only available
        userType: reportAuthor?.type,
        venueName: selectedVenueBranch?.name ?? '',
        harmIdentifiedBy: reportAuthor?.id ?? '',
        harmDate: new Date(),
        harmTime: dayjs().format('HH:mm'),
        harmInstance: '1st',
        nickname: '',
        description: props.player?.description ?? '',
        currentUninterruptedTime: formattedTimeValues.currentUninterruptedTime,
        gamblingAtmUse: props.player?.totalWithdrawals.toString() ?? '00  ',
        currentConsecutiveSweeps: props.consecutiveSweeps ?? '0',
        totalInterventionsOffered: '00',
        totalInterventionsRequested: '00',
        totalTimeInGambling: formattedTimeValues.totalTimeInGambling,
        totalTimeOutside: formattedTimeValues.totalTimeOutside,
        lengthOfPlay: [],
        money: [],
        behaviour: [],
        other: [],
        notesObservation: '',
        createdBySignature: null,
        interventionDecision: null,
        comments: '',
        reviewedBySignature: null,
      };

  const [formState, setFormState] = useState<FormState>(initialFormState);

  const playerFaceImage: string | undefined = props.data
    ? props.data.playerProfile.faceImage
    : props.player?.faceImage;
  const playerBodyImage: string | undefined = props.data
    ? props.data.playerProfile.bodyImage
    : props.player?.fullImage;

  // Handlers
  const handleInputChange = (name: string, value: string) => {
    setFormState((prev: FormState) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (
    field: keyof FormState,
    value: string | string[],
  ) => {
    setFormState((prev: FormState) => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (date?: Date) => {
    setFormState((prev: FormState) => ({ ...prev, harmDate: date }));
  };

  const handleTimeChange = (time: string) => {
    setFormState((prev: FormState) => ({ ...prev, harmTime: time }));
  };

  const handleRadioChange = (value: string) => {
    setFormState((prev: FormState) => ({
      ...prev,
      interventionDecision: value,
    }));
  };

  const handleSubmit = async () => {
    if (!isFormFullyValid(formState)) return;
    try {
      setIsLoading(true);

      if (!reportAuthor || !formState.createdBySignature) {
        return;
      }

      let playerProfile: PlayerProfile = {
        id:
          (props.data ? props.data.playerProfile.id : props.player?.playerId) ??
          '',
      };

      if (props.data) {
        playerProfile.faceImageId = props.data.playerProfile.faceImageId;
        playerProfile.bodyImageId = props.data.playerProfile.bodyImageId;
      } else if (props.player) {
        playerProfile.faceImageId = `players/face_${props.player.playerId}_${props.player.firstEventId}.jpeg`;
        playerProfile.bodyImageId = `players/full_${props.player.playerId}_${props.player.firstEventId}.jpeg`;
      }

      const createdBySignature = {
        ...formState.createdBySignature,
        notes: formState.notesObservation,
      };

      const reviewedBySignature = formState.reviewedBySignature
        ? {
            ...formState.reviewedBySignature,
            notes: formState.comments,
          }
        : undefined;

      // TODO: Add this to service instead
      const isInterventionRequired =
        formState.interventionDecision === 'yes' ? true : false;
      const interventionDetails =
        formState.harmInstance === '1st'
          ? {
              interventionRequired: isInterventionRequired,
              interventionDecision: InterventionDecision.PENDING,
              interventionStatus: InterventionStatus.PENDING,
            }
          : isInterventionRequired
            ? {
                interventionRequired: true,
                interventionDecision: InterventionDecision.REQUIRED,
                interventionStatus: InterventionStatus.PENDING,
              }
            : {
                interventionRequired: false,
                interventionDecision: InterventionDecision.NOT_REQUIRED,
                interventionStatus: InterventionStatus.NOT_APPLICABLE,
              };

      const reportService = new ReportService();

      if (props.data) {
        // TODO: Need to update Harm Identified By if editted
        const harmReportPayload: HarmReport = {
          ...props.data,
          identifiedAt: new Date(
            combineDateAndTime(
              selectedDate.toDate(),
              time ? time.format('HH:mm') : dayjs(new Date()).format('HH:mm'),
            ),
          ),
          playerProfile: {
            ...props.data.playerProfile,
            faceImage: undefined,
            bodyImage: undefined,
          },
          playerDetails: {
            name: formState.nickname,
            description: formState.description,
            gambling: props.data.playerDetails.gambling,
          },
          signsOfGamblingHarm: {
            lengthOfPlay: formState.lengthOfPlay,
            money: formState.money,
            behaviour: formState.behaviour,
            other: formState.other,
          },
          createdBy: createdBySignature,
          reviewedBy: reviewedBySignature,
          intervention: {
            required: interventionDetails.interventionRequired,
            decision: interventionDetails.interventionDecision,
            status: interventionDetails.interventionStatus,
          },
        };

        const response: UpdateHarmReportResponse =
          await reportService.editHarmReport({
            originalHarmReportId: props.data.originalReportId ?? '',
            harmReport: harmReportPayload,
          });
        console.log(response);

        if (props.isReviewAndEditMode) {
          toastV2.success('Review completed successfully');
        } else {
          toastV2.success('Harm Report was updated successfully');
        }
        props.onSuccess?.();
      } else {
        const apiRequestPayload: CreateHarmReportRequest = Object.assign(
          {
            venueBranchId: selectedVenueBranch?._id || '',
            identifiedBy: formState.harmIdentifiedBy,
            identifiedAt: combineDateAndTime(
              formState.harmDate,
              formState.harmTime,
            ),
            playerProfile,
            playerDetails: {
              name: formState.nickname,
              description: formState.description,
              gambling: {
                uninterruptedTime: timeStringToMinutes(
                  formState.currentUninterruptedTime,
                ),
                gamblingATMUse: Number(formState.gamblingAtmUse),
                consecutiveSweepsCount: Number(props.consecutiveSweeps),
                interventionsOffered: Number(props.interventionOffered),
                interventionsRequested: Number(props.interventionRequested),
                totalTimeInGameArea: timeStringToMinutes(
                  formState.totalTimeInGambling,
                ),
                totalTimeOutsideGameArea: timeStringToMinutes(
                  formState.totalTimeOutside,
                ),
              },
            },
            signsOfGamblingHarm: {
              lengthOfPlay: formState.lengthOfPlay,
              money: formState.money,
              behaviour: formState.behaviour,
              other: formState.other,
            },
            createdBy: createdBySignature,
            createdAt: formState.createdBySignature.signedDate.toString(),
            reviewedBy: reviewedBySignature,
          },
          interventionDetails,
        );

        console.log(apiRequestPayload);
        const response: CreateReportResponse =
          await reportService.createHarmReport(apiRequestPayload);

        console.log(response);
        toastV2.success('Harm Report was created successfully');
        props.onSuccess?.();
      }
    } catch (error) {
      console.error(error);
      toastV2.error('Failed to create Harm Report');
    } finally {
      setIsLoading(false);
      props.onBack();
    }
  };

  return (
    <FullscreenModal
      show={true}
      onBack={props.onBack}
      title={props.title}
      modalStyle={styles.modal}
      headerRight={
        <div className={styles.headerRight}>
          <Button className={styles.outlinedButton} onClick={props.onBack}>
            <Trans i18nKey={'createHarmReport.cancel'} />
          </Button>
          <Button
            className={styles.button}
            onClick={handleSubmit}
            disabled={!isFormFullyValid(formState)}
          >
            {isLoading ? (
              <ButtonSpinner />
            ) : (
              <Trans i18nKey={'createHarmReport.submit'} />
            )}
          </Button>
        </div>
      }
    >
      <SignatureModal
        isOpen={showCompletedByAddSignature}
        handleAddSignature={(data: ReportSignature) => {
          setFormState((prev: FormState) => ({
            ...prev,
            createdBySignature: data,
          }));
          setShowCompletedByAddSignature(false);
        }}
        loading={false}
        editingSignature={formState.createdBySignature}
        onClose={() => setShowCompletedByAddSignature(false)}
        userDetail={reportAuthorWithSignature}
        isReviewing={false}
      />
      <SignatureModal
        isOpen={showReviewedByAddSignature}
        handleAddSignature={(data: ReportSignature) => {
          setFormState((prev: FormState) => ({
            ...prev,
            reviewedBySignature: data,
          }));
          setShowReviewedByAddSignature(false);
        }}
        loading={false}
        editingSignature={formState.reviewedBySignature}
        onClose={() => setShowReviewedByAddSignature(false)}
        userDetail={reportAuthorWithSignature}
        isReviewing={true}
      />
      <div className={styles.content}>
        {/* General Info */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createHarmReport.generalInfo'} /> *
          </div>
          <div className={styles.inputs}>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createHarmReport.venue'} />
              </label>
              <Input
                value={formState.venueName}
                disabled
                placeholder="Venue Name"
                type="text"
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createHarmReport.harmIdentifiedBy'} />
              </label>
              {isAutoGenerated ? (
                <Input
                  value={t('createHarmReport.autoDetected') || 'Auto-detected'}
                  disabled
                  placeholder=""
                  type="text"
                />
              ) : (
                <MultiSelectDropdown
                  options={
                    reportAuthor && usersSummary
                      ? [
                          {
                            label: `${reportAuthor?.firstName} ${reportAuthor?.lastName}`,
                            value: reportAuthor?.id ?? '',
                          },
                          ...usersSummary.data
                            .filter(
                              (user: GetUsersResponse) =>
                                user.id && user.id !== reportAuthor?.id,
                            )
                            .map((user: GetUsersResponse) => ({
                              label: `${user.firstName} ${user.lastName}`,
                              value: user.id ?? '',
                            })),
                        ].filter((option: Option) => option.value)
                      : []
                  }
                  search
                  selected={formState.harmIdentifiedBy}
                  onSelect={(val: string | string[]) =>
                    handleSelectChange('harmIdentifiedBy', val as string)
                  }
                  placeholder="Firstname Lastname"
                />
              )}
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createHarmReport.harmIdentifiedOn'} />
              </label>
              <div className={styles.inputGroup}>
                {isAutoGenerated ? (
                  <Input
                    value={moment(formState.harmDate).format('DD/MM/YYYY')}
                    disabled
                    placeholder=""
                    type="text"
                  />
                ) : (
                  <DatePickerV2
                    finalRange={{
                      from: formState.harmDate,
                      to: undefined,
                    }}
                    handleApply={(range: DateRange) => {
                      handleDateChange(range.from);
                      setSelectedDate(dayjs(range.from));
                    }}
                    mode={Mode.Single}
                  />
                )}
                <TimePickerV3
                  date={selectedDate}
                  isDateToday={isSameDayAsToday(selectedDate)}
                  value={time}
                  onChange={(time: Dayjs | null) => {
                    if (time) handleTimeChange(time.format('HH:mm'));
                    setTime(time);
                  }}
                  disabled={isAutoGenerated}
                  ampm={true}
                />
              </div>
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createHarmReport.harmInstance'} />
              </label>
              <Input
                value={
                  props.harmCount
                    ? decimalToOrdinal(props.harmCount + 1)
                    : formState.harmInstance
                }
                disabled
                placeholder="Number of harm done by player"
                type="text"
              />
            </div>
          </div>
        </div>

        {/* Player Photos */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createHarmReport.playersPhoto'} />
          </div>
          <div className={styles.portrait}>
            <label className={styles.label}>
              <Trans i18nKey={'createHarmReport.portrait'} />
            </label>
            {playerFaceImage && <img src={playerFaceImage} alt="Face" />}
          </div>

          <div className={styles.fullBody}>
            <label className={styles.label}>
              <Trans i18nKey={'createHarmReport.fullBody'} />
            </label>
            {playerBodyImage && <img src={playerBodyImage} alt="Full" />}
          </div>
        </div>

        {/* Player Details */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createHarmReport.playersDetails'} />
          </div>
          <div className={styles.inputs}>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createHarmReport.nickName'} />
              </label>
              <Input
                value={formState.nickname}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('nickname', event.target.value)
                }
                placeholder="Enter Name"
                type="text"
              />
            </div>
            <div className={styles.input} style={{ gridColumn: 'span 2' }}>
              <label className={styles.label}>
                <Trans i18nKey={'createHarmReport.description'} /> *
              </label>
              <Input
                value={formState.description}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('description', event.target.value)
                }
                placeholder="Enter Description"
                type="text"
              />
            </div>

            {/* Disabled fields */}
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createHarmReport.currentUninterrupted'} />
              </label>
              <Input
                placeholder=""
                type="text"
                value={formState.currentUninterruptedTime}
                disabled
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createHarmReport.gamblingAtmUse'} />
              </label>
              <Input
                placeholder=""
                type="text"
                value={formState.gamblingAtmUse}
                disabled
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createHarmReport.currentConsecutive'} />
              </label>
              <Input
                placeholder=""
                type="text"
                value={String(formState.currentConsecutiveSweeps)}
                disabled
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createHarmReport.totalIntervention'} />
              </label>
              <Input
                placeholder=""
                type="text"
                value={
                  (props.interventionOffered
                    ? props.interventionOffered.toString().padStart(2, '0')
                    : '00') +
                  '/' +
                  (props.interventionRequested
                    ? props.interventionRequested.toString().padStart(2, '0')
                    : '00')
                }
                disabled
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createHarmReport.totalTimeInGambling'} />
              </label>
              <Input
                placeholder=""
                type="text"
                value={formState.totalTimeInGambling}
                disabled
              />
            </div>
            <div className={styles.input}>
              <label className={styles.label}>
                <Trans i18nKey={'createHarmReport.totalTimeOutside'} />
              </label>
              <Input
                placeholder=""
                type="text"
                value={formState.totalTimeOutside}
                disabled
              />
            </div>
          </div>
        </div>

        {/* Signs of Gambling Harm */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createHarmReport.signsOfGamblingHarm'} /> *
          </div>
          <div className={styles.inputs}>
            <div className={styles.input}>
              <label className={`${styles.label} ${styles.labelWithIcon}`}>
                <img src={clock} alt="" />{' '}
                <Trans i18nKey={'createHarmReport.lengthOfPlay'} />
              </label>
              <MultiSelectDropdown
                options={lengthOfPlayOptions}
                selected={formState.lengthOfPlay}
                onSelect={(vals: string | string[]) =>
                  handleSelectChange('lengthOfPlay', vals as string[])
                }
                isMulti
                placeholder="Select all that apply"
                disabled={isAutoGenerated}
              />
            </div>
            <div className={styles.input}>
              <label className={`${styles.label} ${styles.labelWithIcon}`}>
                <img src={money} alt="" />{' '}
                <Trans i18nKey={'createHarmReport.money'} />
              </label>
              <MultiSelectDropdown
                options={moneyOptions}
                selected={formState.money}
                onSelect={(vals: string | string[]) =>
                  handleSelectChange('money', vals as string[])
                }
                isMulti
                placeholder="Select all that apply"
                disabled={isAutoGenerated}
              />
            </div>
            <div className={styles.input}>
              <label className={`${styles.label} ${styles.labelWithIcon}`}>
                <img src={face} alt="" />{' '}
                <Trans i18nKey={'createHarmReport.behaviour'} />
              </label>
              <MultiSelectDropdown
                options={behaviourOptions}
                selected={formState.behaviour}
                onSelect={(vals: string | string[]) =>
                  handleSelectChange('behaviour', vals as string[])
                }
                isMulti
                placeholder="Select all that apply"
                disabled={isAutoGenerated}
              />
            </div>
            <div className={styles.input}>
              <label className={`${styles.label} ${styles.labelWithIcon}`}>
                <img src={dice} alt="" />
                <Trans i18nKey={'createHarmReport.other'} />
              </label>
              <MultiSelectDropdown
                options={otherOptions}
                selected={formState.other}
                onSelect={(vals: string | string[]) =>
                  handleSelectChange('other', vals as string[])
                }
                isMulti
                placeholder="Select all that apply"
                disabled={isAutoGenerated}
              />
            </div>
          </div>
        </div>

        {/* Notes & Observations */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createHarmReport.notes&Observation'} /> *
          </div>
          <div className={styles.notesSection}>
            <label className={styles.label}>
              <Trans
                i18nKey={'createHarmReport.notes&ObservationOptions.whatWeSee'}
              />
            </label>
            <textarea
              name="notesObservation"
              placeholder="Enter details"
              className={styles.details}
              value={formState.notesObservation}
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                handleInputChange('notesObservation', event.target.value)
              }
            />
            <ul>
              <li className={styles.bulletPoint}>
                •{' '}
                <Trans
                  i18nKey={
                    'createHarmReport.notes&ObservationOptions.beSpecific'
                  }
                />
              </li>
              <li className={styles.bulletPoint}>
                •{' '}
                <Trans
                  i18nKey={
                    'createHarmReport.notes&ObservationOptions.playerDescriptions'
                  }
                />
              </li>
              <li className={styles.bulletPoint}>
                •{' '}
                <Trans
                  i18nKey={
                    'createHarmReport.notes&ObservationOptions.describeHow'
                  }
                />
              </li>
            </ul>
          </div>
        </div>

        {/* Created By */}
        <div className={styles.cell}>
          <div className={styles.title}>
            <Trans i18nKey={'createHarmReport.createdBy'} /> *
          </div>
          <div className={styles.inputs}>
            {isAutoGenerated ? (
              <div className={styles.signaturePreview}>
                <div className={styles.details}>
                  <div className={styles.userDetail}>
                    {t('reportDetails.autogeneratedReport') ||
                      'Autogenerated Report'}
                  </div>
                  <div className={styles.userDetail}>
                    {moment(props.data?.createdAt).format(
                      'DD/MM/YYYY, hh:mm A',
                    )}
                  </div>
                </div>
              </div>
            ) : formState.createdBySignature ? (
              <div className={styles.signaturePreview}>
                <div className={styles.details}>
                  <img
                    src={formState.createdBySignature.signature}
                    alt="signature"
                    width={150}
                  />
                  <div className={styles.userDetail}>
                    {formState.createdBySignature.firstName}{' '}
                    {formState.createdBySignature.lastName}
                  </div>
                  <div className={styles.userDetail}>
                    {moment(formState.createdBySignature.signedDate).format(
                      'DD/MM/YYYY, hh:mm A',
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <Button
                className={styles.button}
                onClick={() => setShowCompletedByAddSignature(true)}
                disabled={
                  !hasSectionMandatoryFields(formState).hasCommonMandatory
                }
              >
                <Trans i18nKey={'createHarmReport.addSignature'} />
              </Button>
            )}
          </div>
        </div>

        {/* Manager Section visible only for userType !== VENUE_STAFF and not auto-generated 1st instance */}
        {reportAuthor?.type !== UserType.VENUE_STAFF &&
          !(isAutoGenerated && formState.harmInstance === '1st') && (
            <>
              {/* Manager Review */}
              <div className={`${styles.cell} ${styles.managerCell}`}>
                <div className={styles.Heading}>
                  <Trans i18nKey={'createHarmReport.managerReview'} />
                </div>
              </div>

              {/* Intervention Decision */}
              <div className={`${styles.cell} ${styles.managerCell}`}>
                <div className={styles.title}>
                  <Trans i18nKey={'createHarmReport.interventionDecision'} />
                </div>
                <div className={styles.radioSection}>
                  <label className={styles.label}>
                    <Trans i18nKey={'createHarmReport.requireIntervention'} />
                  </label>
                  <RadioGroupV2
                    options={[
                      { label: 'Yes', value: 'yes' },
                      { label: 'No', value: 'no' },
                    ]}
                    name="compliance"
                    value={formState.interventionDecision ?? ''}
                    onChange={handleRadioChange}
                    horizontal
                  />
                </div>
              </div>

              {/* Comments */}
              <div className={`${styles.cell} ${styles.managerCell}`}>
                <div className={styles.title}>
                  <Trans i18nKey={'createHarmReport.comments'} />
                </div>
                <div className={styles.notesSection}>
                  <textarea
                    name="comments"
                    placeholder="Enter details"
                    className={styles.details}
                    value={formState.comments}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                      handleInputChange('comments', event.target.value)
                    }
                  />
                </div>
              </div>

              {/* Reviewed By */}
              <div className={`${styles.cell} ${styles.managerCell}`}>
                <div className={styles.title}>
                  <Trans i18nKey={'createHarmReport.reviewedBy'} /> *
                </div>
                <div className={styles.inputs}>
                  {formState.reviewedBySignature ? (
                    <div className={styles.signaturePreview}>
                      <div className={styles.details}>
                        <img
                          src={formState.reviewedBySignature.signature}
                          alt="signature"
                          width={150}
                        />
                        <div className={styles.userDetail}>
                          {formState.reviewedBySignature.firstName}{' '}
                          {formState.reviewedBySignature.lastName}
                        </div>
                        <div className={styles.userDetail}>
                          {moment(
                            formState.reviewedBySignature.signedDate,
                          ).format('DD/MM/YYYY, hh:mm A')}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Button
                      className={styles.button}
                      onClick={() => setShowReviewedByAddSignature(true)}
                      disabled={
                        !hasSectionMandatoryFields(formState)
                          .hasManagerMandatory
                      }
                    >
                      <Trans i18nKey={'createHarmReport.addSignature'} />
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
      </div>
    </FullscreenModal>
  );
};

export default HarmReportForm;
